/**
 * Typed client helpers for the /api/* endpoints. All requests are
 * automatically Authorization-stamped by installAuthFetch.
 */

export interface ColumnSchema {
  name: string;
  type: string;
  /** Mockaroo per-field options (e.g. min/max/values). Always serialised. */
  options: Record<string, unknown>;
}

export interface Template {
  id: number;
  name: string;
  description: string | null;
  schema: ColumnSchema[];
  is_builtin: 0 | 1;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface ApiError {
  error: string;
  [k: string]: unknown;
}

async function unwrap<T>(res: Response): Promise<T> {
  const contentType = res.headers.get('content-type') || '';
  const isJson = contentType.includes('json');

  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    if (isJson) {
      try {
        const body = (await res.json()) as ApiError;
        if (body.error) message = body.error;
      } catch {
        /* keep status fallback */
      }
    }
    const e = new Error(message);
    (e as Error & { status: number }).status = res.status;
    throw e;
  }

  // 200 OK but not JSON → typically Vite's SPA fallback (no Pages Functions
  // running). Make the failure mode obvious rather than dumping a raw
  // JSON.parse exception in the UI.
  if (!isJson) {
    const e = new Error(
      'Backend API is not reachable. In local dev, run `npm run dev:full` (requires D1 + KV provisioned per BOOTSTRAP.md §5–7).',
    );
    (e as Error & { status: number }).status = 503;
    throw e;
  }

  return (await res.json()) as T;
}

export const api = {
  async me() {
    const res = await fetch('/api/auth/me', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    return unwrap<{ email: string; name: string; app_role: 'admin' | 'editor' | 'viewer' }>(res);
  },

  async listTemplates() {
    const res = await fetch('/api/templates');
    return unwrap<{ templates: Template[] }>(res);
  },

  async getTemplate(id: number) {
    const res = await fetch(`/api/templates/${id}`);
    return unwrap<{ template: Template }>(res);
  },

  async createTemplate(input: { name: string; description?: string; schema: ColumnSchema[] }) {
    const res = await fetch('/api/templates', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    return unwrap<{ template: Template }>(res);
  },

  async updateTemplate(id: number, input: { name?: string; description?: string; schema?: ColumnSchema[] }) {
    const res = await fetch(`/api/templates/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    return unwrap<{ template: Template }>(res);
  },

  async deleteTemplate(id: number) {
    const res = await fetch(`/api/templates/${id}`, { method: 'DELETE' });
    return unwrap<{ ok: true }>(res);
  },

  /**
   * Generate CSV. Returns a Blob so callers can download it directly.
   * Throws on non-2xx.
   */
  async generate(input: { schema: ColumnSchema[]; count: number; template_id?: number | null }) {
    const res = await fetch('/api/generate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!res.ok) {
      let msg = `${res.status} ${res.statusText}`;
      try {
        const j = (await res.json()) as ApiError;
        msg = j.error || msg;
      } catch {
        // CSV body or empty — keep msg
      }
      throw new Error(msg);
    }
    return res.blob();
  },
};
